import { Component, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs';

interface BreadcrumbSegment {
  label: string;
  route: string | null;
}

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  geofences: 'Geofences',
  orders: 'Orders',
  payments: 'Payments',
  'menu-items': 'Menu Items',
  ingredients: 'Ingredients',
  producers: 'Healthy Gourmet Kitchens',
  'fulfillment-window-settings': 'Fulfillment Window Settings',
  'pricing-floors': 'Pricing Floors',
  membership: 'Membership',
  packages: 'Packages',
  options: 'Options',
  features: 'Features',
  reports: 'Reports',
  entitlements: 'Entitlements',
  'access-management': 'Access Management',
  'accounts-users': 'Accounts & Users',
  groups: 'Groups',
  invitations: 'Invitations',
  create: 'Create',
  edit: 'Edit',
  new: 'New',
  allocations: 'Allocations',
  usages: 'Usages',
  users: 'Users',
  households: 'Households',
  'notification-rules': 'Notification Rules'
};

/**
 * Pages with dynamic segments (e.g. /ingredients/:id) can inject this service
 * and call setLabel() to provide a human-readable breadcrumb for their route param.
 * Call clearLabel() on destroy or when navigating away.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbLabelService {
  /** Map of URL segment value → display label, e.g. { 'abc-123-guid': 'Chicken Breast' } */
  private readonly labels = signal<Record<string, string>>({});

  setLabel(segment: string, label: string): void {
    this.labels.update((prev) => ({ ...prev, [segment]: label }));
  }

  clearLabel(segment: string): void {
    this.labels.update((prev) => {
      const next = { ...prev };
      delete next[segment];
      return next;
    });
  }

  getLabel(segment: string): string | undefined {
    return this.labels()[segment];
  }
}

@Component({
  selector: 'yt-breadcrumb',
  imports: [RouterModule],
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.scss'
})
export class Breadcrumb {
  private readonly router = inject(Router);

  readonly segments = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.buildBreadcrumbs(event.urlAfterRedirects))
    ),
    { initialValue: [] as BreadcrumbSegment[] }
  );

  private buildBreadcrumbs(url: string): BreadcrumbSegment[] {
    const parts = url.split('/').filter(Boolean);
    const segments: BreadcrumbSegment[] = [];
    let accumulatedPath = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      accumulatedPath += '/' + part;

      // Check static labels first, then dynamic labels from pages
      const label = ROUTE_LABELS[part];
      if (!label) continue;

      const isLast = i === parts.length - 1;
      segments.push({
        label,
        route: isLast ? null : accumulatedPath
      });
    }

    return segments;
  }
}
